/**
 * A styled readonly input with UI for copying to clipboard
 */
import React, {
  FunctionComponent, useRef, useState, forwardRef, useEffect,
} from 'react';
import styled from 'styled-components';
import { Flex, Box } from 'rebass/styled-components';
import theme from 'styled-theming';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  CopyIconActive, CopyIcon, Name, CopiedMessage,
} from './Row';

const TIME_TO_SHOW_COPIED = 1500;

export interface CopyableTextProps {
  text?: string;
  copyEnabled: boolean;
  rightIcon?: React.ReactElement;
  onClickAdornment?: () => void;
  testId?: string;
}

const backgroundColor = theme('mode', {
  light: (props: any) => props.theme.colors.ghost,
  dark: (props: any) => props.theme.colors.lpGhostSoft,
});

const borderColor = theme('mode', {
  light: (props: any) => props.theme.colors.ghostDark,
  dark: (props: any) => props.theme.colors.lpGhost,
});

const hoverBackground = theme('mode', {
  light: (props: any) => props.theme.colors.ghostDark,
  dark: (props: any) => props.theme.colors.lpGhost,
});

const hoverColor = theme('mode', {
  light: (props: any) => props.theme.colors.deepGrey,
  dark: (props: any) => props.theme.colors.white,
});

const textColor = theme('mode', {
  light: (props: any) => props.theme.colors.lightGrey,
  dark: (props: any) => props.theme.lpColorsDark.graya9,
});

export const ReadOnlyText = styled(Flex)`
  transition: ${(props: any) => props.theme.animation.normal};
  cursor: pointer;
  background-color: ${backgroundColor};
  border-radius: ${(props: any) => props.theme.radius.medium};
  border-color: ${borderColor};
  border-style: solid;
  border-width: ${(props) => '1px'};
  font-size: 14px;
  line-height: 135%;
  color: ${textColor};
  &:hover {
    color: ${hoverColor};
    background-color: ${hoverBackground};
  }
  height: 100%;
  padding: 8px 16px;
  overflow-x: hidden;
`;

const RightAdornment: FunctionComponent<any> = ({ copied, icon, onClick }: any) => {
  if (copied) {
    return (
      <CopiedMessage fontSize={0} ml={3} mr={3}>
        copied to clipboard
      </CopiedMessage>
    );
  }
  return (
    <Box onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    }}
    >
      {icon}
    </Box>
  );
};

export interface IFlexContentProps {
  text?: string;
  copyEnabled: boolean;
  copied: boolean;
  rightIcon: React.ReactNode;
  onClickAdornment?: () => void;
  testId?: string;
}

const FlexContent: FunctionComponent<IFlexContentProps> = forwardRef(({
  text,
  copyEnabled,
  copied,
  rightIcon,
  onClickAdornment,
  testId = 'copyable-text-content',
}: IFlexContentProps, ref) => (
  <ReadOnlyText ref={ref}>
    {(copyEnabled && text) && (
      <Flex flexDirection="column" justifyContent="center" alignItems="center" mr={3}>{copied ? <CopyIconActive size={18} /> : <CopyIcon size={18} />}</Flex>
    )}
    <Flex alignItems="center" flex={1}>
      <Name title={text} data-testid={testId}>{text}</Name>
      <RightAdornment copied={copied} icon={rightIcon} onClick={onClickAdornment} />
    </Flex>
  </ReadOnlyText>
));

const CopyableText: FunctionComponent<CopyableTextProps> = ({
  text,
  copyEnabled,
  rightIcon,
  onClickAdornment,
  testId,
}: CopyableTextProps) => {
  const [copied, setCopied] = useState(false);

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return (copyEnabled && text) ? (
    <CopyToClipboard
      text={text}
      onCopy={() => {
        if (isMounted.current) {
          setCopied(true);
        }
        setTimeout(() => {
          if (isMounted.current) {
            setCopied(false);
          }
        }, TIME_TO_SHOW_COPIED);
      }}
    >
      <Box height="100%">
        <FlexContent
          text={text}
          copyEnabled={copyEnabled}
          copied={copied}
          rightIcon={rightIcon}
          onClickAdornment={onClickAdornment}
          testId={testId}
        />
      </Box>
    </CopyToClipboard>
  ) : (
    <FlexContent
      text={text}
      copyEnabled={copyEnabled}
      copied={copied}
      rightIcon={rightIcon}
      onClickAdornment={onClickAdornment}
      testId={testId}
    />
  );
};

export default CopyableText;
